// The form view — a slide-up overlay over chat behind the pinned card's OPEN
// action. Sticky section stepper, grouped section cards of FieldRows, and the
// end card with the always-tappable Review & submit (a blocked submit marks and
// scrolls; a clean one runs the sending → submitted / failed machinery).
//
// Closing is lossless (values live in the shared state the chat fill writes),
// Esc closes the topmost layer (picker first, then the overlay), and Discard
// defers to the pinned card's existing confirm. The per-field help sheet is
// slice 5 and does not mount here.

import { FocusEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AddressBag, addressFillsFor } from './addressFill';
import {
    REVIEW_CLEAN_LINE,
    REVIEW_DIRTY_LINE,
    SUBMIT_FAILURE_MESSAGE,
    UPDATE_SUBMISSION_LABEL,
} from './constants';
import styles from './ConversationalForms.module.css';
import FieldHelpSheet from './FieldHelpSheet';
import {
    HELP_LOCK_FILLED_MESSAGE,
    HELP_LOCK_SUGGESTED_MESSAGE,
    HELP_LOCK_UNAVAILABLE_MESSAGE,
} from './FieldHelpThread';
import FieldRow from './FieldRow';
import { useConversationalForms } from './FormsContext';
import { openForAI, progressPct, statusOf, visibleFields } from './formsSelectors';
import { HELP_HOST } from './helpHostConfig';
import { AlertIcon, CheckIcon, ChevronLeftIcon } from './icons';
import PickerSheet from './PickerSheet';
import SectionStepper, { StepperSection } from './SectionStepper';
import { scrollToElement } from './smoothScroll';
import { CfField, FieldStatus, FieldValue } from './types';
import { useEscLayer, useFocusTrap } from './useFocusTrap';
import { validateFormat, validateForSubmit } from './validation';
import { sameValue } from './valueFormat';

/** Scroll offset below the scroller's top at which a section becomes active. */
const ACTIVE_SECTION_OFFSET = 80;

/**
 * Field help (slice 5, adversarial-review B1b): why the composer is locked
 * for a field that's visible (open, or has session history) but not
 * currently extraction-eligible. `undefined` means not locked. A field can
 * land here confirmed (filled — via the thread or otherwise), suggested (a
 * pending suggestion the row's own Confirm/Undo owns), or neither (hidden by
 * a conditional parent, or opted out of bot collection) — the last case
 * shares one generic message since neither is actionable from the thread.
 */
const lockedMessageFor = (eligibleNow: boolean, status: FieldStatus): string | undefined => {
    if (eligibleNow) return undefined;
    if (status === 'suggested') return HELP_LOCK_SUGGESTED_MESSAGE;
    if (status === 'confirmed') return HELP_LOCK_FILLED_MESSAGE;
    return HELP_LOCK_UNAVAILABLE_MESSAGE;
};

type SubmitState = 'idle' | 'sending' | 'failed' | 'submitted';

type FormOverlayProps = {
    onClose: () => void;
};

const FormOverlay = ({ onClose }: FormOverlayProps) => {
    const {
        form: pinnedForm,
        registry,
        state,
        dispatch,
        confirmSuggestion,
        undoSuggestion,
        requestDiscard,
        submitForm,
        completeSubmission,
        reviewFormId,
        updateSubmission,
        completeUpdate,
        helpFieldId,
        helpThreads,
        openHelp,
        closeHelp,
        sendHelp,
        retryHelp,
        helpBusyFieldIds,
        helpSlowFieldIds,
    } = useConversationalForms();

    // Forms-corrections slice (D1): review mode is this same overlay showing
    // a submitted form instead of the pinned/active one — `reviewFormId` is
    // independent of `activeFormId`, so the display form resolves from it
    // first. `dirty` only means anything while reviewing.
    const reviewing = reviewFormId !== null;
    const form = reviewFormId !== null ? registry?.byId.get(reviewFormId) ?? null : pinnedForm;
    const dirty = reviewFormId !== null && state.dirtyFormIds.includes(reviewFormId);

    const [editingField, setEditingField] = useState<string | null>(null);
    const [pickerFieldId, setPickerFieldId] = useState<string | null>(null);
    const [activeSectionId, setActiveSectionId] = useState<string | null>(null);
    const [submitState, setSubmitState] = useState<SubmitState>('idle');
    const [submitAttempted, setSubmitAttempted] = useState(false);
    const [blockedCount, setBlockedCount] = useState(0);

    const scrollRef = useRef<HTMLDivElement>(null);
    const sectionRefs = useRef<Record<string, HTMLElement | null>>({});

    // A submit/update await can outlive the overlay (Esc during 'sending'
    // unmounts it; the in-flight POST still resolves). The context-level
    // dispatches it triggers (`completeUpdate`, `onClose`) are safe regardless
    // — they target the provider, which outlives this component — but this
    // component's own `setSubmitState` is not, so its post-await calls check
    // this ref first.
    const mountedRef = useRef(true);
    useEffect(() => () => {
        mountedRef.current = false;
    }, []);

    // Trap + restore: Tab wraps within the overlay, initial focus lands on the
    // back button (the first focusable) after the trap's tick, and closing —
    // however it happens — returns focus to whatever opened the overlay: the
    // pinned card's OPEN control.
    const trapRef = useFocusTrap<HTMLDivElement>();

    // Esc peels the topmost layer; the overlay registers on mount, so any
    // layer opened above it (the picker) takes precedence per press.
    useEscLayer(onClose);

    // The registry lets parent chains resolve across forms — a hidden
    // cross-form-parented field leaves rendering, counts, and progress alike
    // (platform contract, tracker 6.26b).
    const fields = useMemo(() => visibleFields(form, state, registry), [form, state, registry]);

    // Computed from the *display* form (review or pinned), not the context's
    // `pct` — that always tracks the pinned/active form, which is the wrong
    // one while reviewing a different submitted form.
    const pct = useMemo(() => progressPct(form, state, registry), [form, state, registry]);

    // The child treatment (inset + indent) reads as "conditional on the row
    // above", so it applies only when the parent renders in this same form; a
    // cross-form-parented field has no parent row here and renders plain.
    const ownIds = useMemo(() => new Set(form?.fields.map((f) => f.id)), [form]);

    const sections: StepperSection[] = useMemo(() => {
        if (!form) return [];
        return form.sections
            .map((section) => ({
                ...section,
                fields: fields.filter((f) => f.sectionId === section.id),
            }))
            .filter((section) => section.fields.length > 0);
    }, [form, fields]);

    // Resolve a field definition so statusOf can apply structural completeness
    // (a partial consent or group never reads confirmed — design D1).
    const fieldFor = useCallback(
        (fieldId: string): CfField | undefined =>
            registry?.fieldsById.get(fieldId) ?? form?.fields.find((f) => f.id === fieldId),
        [registry, form]
    );

    const isConfirmed = useCallback(
        (fieldId: string) => statusOf(state, fieldId, fieldFor(fieldId)) === 'confirmed',
        [state, fieldFor]
    );

    // Field help (slice 5) — affordance eligibility (design D6/D7, lead
    // clarification, hardened per adversarial review B1). `openForAI`
    // already excludes confirmed and `noAI` fields (which subsumes
    // `consent`/`group`/`longtext` — formsConfig's `NEVER_AI`); review mode
    // and a pending suggestion are not things `openForAI` encodes, so they're
    // checked explicitly. Eligibility alone is NOT what gates the control's
    // VISIBILITY, though (B1a): a field that leaves the eligible set mid-
    // thread — filled, hidden by a parent change — must keep its control
    // reachable while the thread is open OR has session history, so (1) the
    // delta spec's "reopening shows the prior exchange" holds even after a
    // fill, and (2) the control's own DOM node survives long enough for the
    // sheet/inline trap to restore focus to it on close (a field that JUST
    // got filled through the thread is confirmed by the time the student
    // closes it — losing the control right then, in the same render, is what
    // silently broke that restore before this fix). A PENDING SUGGESTION is
    // the one exception `hasHistory` does NOT override (adversarial-review
    // R2): the delta spec's "Suggestion suppresses the button" scenario is
    // unconditional — Confirm/Undo on the row owns that moment even if the
    // suggestion arrived through this very thread, so `visible` never reads
    // `hasHistory` while `status === 'suggested'`.
    const openForAIIds = useMemo(
        () => new Set(openForAI(form, state, registry).map((f) => f.id)),
        [form, state, registry]
    );
    const helpAffordance = useCallback(
        (field: CfField, status: FieldStatus, hasHistory: boolean) => {
            if (reviewing || field.type === 'consent' || field.noAI) {
                return { visible: false, lockedMessage: undefined as string | undefined };
            }
            const eligibleNow = status !== 'suggested' && openForAIIds.has(field.id);
            const isOpen = helpFieldId === field.id;
            const visible = eligibleNow || isOpen || (hasHistory && status !== 'suggested');
            return { visible, lockedMessage: visible ? lockedMessageFor(eligibleNow, status) : undefined };
        },
        [reviewing, openForAIIds, helpFieldId]
    );

    // Activating Ask-AI opens the thread and keeps the field in view (delta
    // spec: "activating it SHALL open the field's help thread scrolled to
    // keep the field visible") — same scroll helper `trySubmit` uses to bring
    // a blocked field into view.
    const handleAskHelp = useCallback(
        (fieldId: string) => {
            openHelp(fieldId);
            const el = scrollRef.current?.querySelector<HTMLElement>(`[data-testid="cf-row-${fieldId}"]`);
            scrollToElement(scrollRef.current, el);
        },
        [openHelp]
    );

    const helpField = helpFieldId ? fieldFor(helpFieldId) : undefined;
    const helpSectionTitle = helpField
        ? form?.sections.find((s) => s.id === helpField.sectionId)?.title ?? 'General'
        : '';
    // The sheet mounts independently of the field-row loop below (a field
    // hidden by a conditional parent mid-thread has no row at all, but its
    // sheet must still show — and lock — correctly), so its locked-state
    // needs its own computation rather than borrowing the per-row one.
    const helpFieldLockedMessage = useMemo(() => {
        if (!helpField) return undefined;
        const status = statusOf(state, helpField.id, helpField);
        const eligibleNow = status !== 'suggested' && openForAIIds.has(helpField.id);
        return lockedMessageFor(eligibleNow, status);
    }, [helpField, state, openForAIIds]);

    const handleScroll = useCallback(() => {
        const scroller = scrollRef.current;
        if (!scroller || !sections.length) return;
        const threshold = scroller.getBoundingClientRect().top + ACTIVE_SECTION_OFFSET;
        let current = sections[0].id;
        sections.forEach((section) => {
            const el = sectionRefs.current[section.id];
            if (el && el.getBoundingClientRect().top <= threshold) current = section.id;
        });
        setActiveSectionId(current);
    }, [sections]);

    const jumpToSection = useCallback((sectionId: string) => {
        // Programmatic, scale-aware scroll (task 1.3) — native smooth
        // scrollIntoView misfires inside the widget's transform:scale wrapper.
        scrollToElement(scrollRef.current, sectionRefs.current[sectionId]);
        setActiveSectionId(sectionId);
    }, []);

    // Mobile keyboard rule (design risk note / handoff "Responsive / shells"):
    // when the software keyboard opens over the full-bleed widget, bring the
    // focused field into view by scrolling the overlay's own scroller — never
    // the window. Desktop keeps the browser's native behaviour.
    const handleFieldFocus = useCallback((e: FocusEvent<HTMLDivElement>) => {
        if (!window.matchMedia || !window.matchMedia('(max-width: 768px)').matches) return;
        const target = e.target as HTMLElement;
        if (!target.matches?.('input, textarea')) return;
        const row = target.closest<HTMLElement>('[data-testid^="cf-row-"]') ?? target;
        scrollToElement(scrollRef.current, row);
    }, []);

    const handleChange = useCallback(
        (fieldId: string, value: FieldValue) => {
            // Forms-corrections (D2, hardened): any edit on a submitted form
            // flips it dirty — sticky until a successful update, never
            // cleared by closing or by editing back to the original value —
            // but only when the incoming value actually differs from what's
            // stored now. A no-op gesture (re-tapping the already-selected
            // radio, re-picking the current value) must never dirty a clean
            // review. Compared against the pre-dispatch value.
            const changed = !sameValue(state.values[fieldId], value);
            dispatch({ type: 'setValue', fieldId, value });
            if (submitState === 'failed') setSubmitState('idle');
            if (reviewFormId && changed) dispatch({ type: 'setDirty', formId: reviewFormId });
        },
        [dispatch, submitState, reviewFormId, state.values]
    );

    const handleDoneEdit = useCallback(
        (fieldId: string) => {
            setEditingField((current) => (current === fieldId ? null : current));
            const field = form?.fields.find((f) => f.id === fieldId);
            if (!field) return;
            const error = validateFormat(field, state.values[fieldId]);
            dispatch({ type: 'setFieldError', fieldId, message: error });
        },
        [form, state.values, dispatch]
    );

    // One Places pick fills the address field and its label-matched siblings
    // in the same form — each lands as an ordinary confirmed scalar, so they
    // collapse together in the one action (delta "Places pick multi-fills").
    const handleAddressPick = useCallback(
        (fieldId: string, bag: AddressBag) => {
            if (!form) return;
            const fills = addressFillsFor(form, fieldId, bag);
            const changed = fills.some((fill) => !sameValue(state.values[fill.fieldId], fill.value));
            fills.forEach((fill) =>
                dispatch({ type: 'setValue', fieldId: fill.fieldId, value: fill.value })
            );
            // The pick ends the edit — the filled rows collapse immediately.
            setEditingField((current) => (current === fieldId ? null : current));
            if (submitState === 'failed') setSubmitState('idle');
            if (reviewFormId && changed) dispatch({ type: 'setDirty', formId: reviewFormId });
        },
        [form, dispatch, submitState, reviewFormId, state.values]
    );

    const pickerField: CfField | undefined = useMemo(
        () => (pickerFieldId ? form?.fields.find((f) => f.id === pickerFieldId) : undefined),
        [form, pickerFieldId]
    );

    const trySubmit = useCallback(async () => {
        if (!form || submitState === 'sending') return;

        const result = validateForSubmit(form, state, registry);
        setSubmitAttempted(true);
        setBlockedCount(result.blockedCount);

        if (result.blockedCount > 0) {
            Object.entries(result.errors).forEach(([fieldId, message]) =>
                dispatch({ type: 'setFieldError', fieldId, message })
            );
            if (result.firstBlockedId) {
                const el = scrollRef.current?.querySelector<HTMLElement>(
                    `[data-testid="cf-row-${result.firstBlockedId}"]`
                );
                // Centre-ish anchoring so the marked row and its error line
                // both land in view (scale-aware — see smoothScroll).
                scrollToElement(scrollRef.current, el, 'center');
            }
            return;
        }

        setSubmitState('sending');
        // Forms-corrections (D3): the update reuses submit semantics wholesale
        // up to this point (validation, sending, failure callout, retry). It
        // diverges only at success — no success overlay, a light chat
        // acknowledgement instead, and dirty clears (`completeUpdate`), never
        // `completeSubmission`'s queue handoff / `markSubmitted`.
        if (reviewing && reviewFormId) {
            const ok = await updateSubmission(reviewFormId);
            if (ok) {
                // Safe unconditionally: these dispatch to the provider, which
                // outlives this component even if Esc unmounted it mid-await.
                completeUpdate(reviewFormId);
                onClose();
                if (mountedRef.current) setSubmitState('idle');
            } else {
                if (mountedRef.current) setSubmitState('failed');
            }
            return;
        }
        const ok = await submitForm();
        if (mountedRef.current) setSubmitState(ok ? 'submitted' : 'failed');
    }, [
        form,
        registry,
        state,
        submitState,
        dispatch,
        submitForm,
        reviewing,
        reviewFormId,
        updateSubmission,
        completeUpdate,
        onClose,
    ]);

    const finishSubmitted = useCallback(() => {
        completeSubmission();
        onClose();
    }, [completeSubmission, onClose]);

    const discardFromOverlay = useCallback(() => {
        requestDiscard();
        onClose();
    }, [requestDiscard, onClose]);

    if (!form) return null;

    // Readiness copy, not submit gating (all-optional forms stay submittable):
    // a required-field form is "all set" when every required field is confirmed
    // (pct 100 under required semantics). An all-optional form has no required
    // gate, so the claim is earned by at least one filled field — an untouched
    // form must never read "ready to review & submit" (store tracker 6.25).
    const filledCount = fields.filter((f) => isConfirmed(f.id)).length;
    const allOptional = fields.length > 0 && fields.every((f) => f.optional);
    const complete = allOptional ? filledCount > 0 : pct === 100;
    const showBlocked = submitAttempted && blockedCount > 0 && submitState === 'idle';

    return (
        <div
            ref={trapRef}
            className={styles.overlay}
            role="dialog"
            aria-modal="true"
            aria-label={form.short}
            data-testid="cf-form-overlay"
        >
            <div className={styles.overlayBar}>
                <button
                    type="button"
                    className={styles.overlayBack}
                    onClick={onClose}
                    aria-label="Back to chat"
                    // The overlay's own trap already treats this as its
                    // focus anchor (the first focusable node, claimed if
                    // nothing else has focus 40ms after mount) — the field-
                    // help hosts' row-focus fallback reuses the same anchor
                    // as a last resort when even the row doesn't exist
                    // (adversarial-review N2b: a conditional parent change
                    // hid the field entirely while its thread was open).
                    data-testid="cf-overlay-back"
                >
                    <ChevronLeftIcon /> Chat
                </button>
                <span className={styles.overlayTitle}>{form.short}</span>
                {reviewing ? (
                    <span className={styles.overlayTag} data-testid="cf-overlay-submitted-tag">
                        <CheckIcon /> Submitted
                    </span>
                ) : (
                    <button type="button" className={styles.overlayDiscard} onClick={discardFromOverlay}>
                        Discard
                    </button>
                )}
            </div>

            {sections.length > 1 && (
                <SectionStepper
                    sections={sections}
                    activeId={activeSectionId ?? sections[0].id}
                    isConfirmed={isConfirmed}
                    onJump={jumpToSection}
                />
            )}

            <div
                className={styles.overlayBody}
                ref={scrollRef}
                onScroll={handleScroll}
                onFocus={handleFieldFocus}
            >
                {sections.map((section, index) => {
                    // Same fallback as progressPct (tracker 6.25): a section with
                    // no required fields counts filled/total instead of a
                    // meaningless "0/0".
                    const required = section.fields.filter((f) => !f.optional);
                    const counted = required.length ? required : section.fields;
                    const done = counted.filter((f) => isConfirmed(f.id)).length;
                    const sectionComplete = counted.length > 0 && done === counted.length;
                    return (
                        <section
                            key={section.id}
                            ref={(el) => {
                                sectionRefs.current[section.id] = el;
                            }}
                            className={styles.section}
                            aria-label={section.title}
                        >
                            <div className={styles.sectionHead}>
                                <span
                                    className={`${styles.sectionNum} ${sectionComplete ? styles.sectionNumDone : ''}`}
                                    aria-hidden="true"
                                >
                                    {sectionComplete ? '✓' : index + 1}
                                </span>
                                <h2 className={styles.sectionTitle}>{section.title}</h2>
                                <span className={styles.sectionCount}>
                                    {done}/{counted.length}
                                </span>
                            </div>
                            {section.blurb && <p className={styles.sectionBlurb}>{section.blurb}</p>}
                            <div className={styles.sectionCard}>
                                {section.fields.map((field) => {
                                    const status = statusOf(state, field.id, field);
                                    return (
                                        <FieldRow
                                            key={field.id}
                                            field={field}
                                            asChild={!!field.parentId && ownIds.has(field.parentId)}
                                            value={state.values[field.id]}
                                            suggestion={state.suggested[field.id]}
                                            status={status}
                                            error={state.fieldErrors[field.id]}
                                            editing={editingField === field.id}
                                            onChange={handleChange}
                                            onConfirm={confirmSuggestion}
                                            onUndo={undoSuggestion}
                                            onEdit={setEditingField}
                                            onDoneEdit={handleDoneEdit}
                                            onOpenPicker={setPickerFieldId}
                                            onAddressPick={handleAddressPick}
                                            help={(() => {
                                                const thread = helpThreads[field.id] ?? [];
                                                const affordance = helpAffordance(field, status, thread.length > 0);
                                                return {
                                                    visible: affordance.visible,
                                                    lockedMessage: affordance.lockedMessage,
                                                    open: helpFieldId === field.id,
                                                    sectionTitle: section.title,
                                                    thread,
                                                    busy: helpBusyFieldIds.has(field.id),
                                                    slow: helpSlowFieldIds.has(field.id),
                                                    onOpen: handleAskHelp,
                                                    onClose: closeHelp,
                                                    onSend: sendHelp,
                                                    onRetry: retryHelp,
                                                };
                                            })()}
                                        />
                                    );
                                })}
                            </div>
                        </section>
                    );
                })}

                <div className={`${styles.endCard} ${showBlocked ? styles.endCardBlocked : ''}`}>
                    {submitState === 'failed' && (
                        <div className={styles.submitFailure} role="alert">
                            <AlertIcon /> {SUBMIT_FAILURE_MESSAGE}
                        </div>
                    )}
                    <p className={styles.endLine}>
                        {showBlocked
                            ? `${blockedCount} required ${blockedCount === 1 ? 'answer' : 'answers'} still needed — marked above.`
                            : reviewing
                                ? (dirty ? REVIEW_DIRTY_LINE : REVIEW_CLEAN_LINE)
                                : complete
                                    ? 'All set — ready to review & submit.'
                                    : `You're ${pct}% done.`}
                    </p>
                    {/* Review mode shows no submit action while clean (spec:
                        "Clean close is free") — it reappears the moment the
                        form goes dirty. */}
                    {(!reviewing || dirty) && (
                        <button
                            type="button"
                            className={styles.submitButton}
                            onClick={trySubmit}
                            disabled={submitState === 'sending'}
                            data-testid="cf-submit"
                        >
                            {submitState === 'sending'
                                ? 'Sending…'
                                : submitState === 'failed'
                                    ? 'Try again'
                                    : reviewing
                                        ? UPDATE_SUBMISSION_LABEL
                                        : 'Review & submit'}
                        </button>
                    )}
                </div>
            </div>

            {pickerField && (
                <PickerSheet
                    field={pickerField}
                    value={String(state.values[pickerField.id] ?? '')}
                    onPick={(value) => {
                        handleChange(pickerField.id, value);
                        dispatch({ type: 'setFieldError', fieldId: pickerField.id, message: null });
                        setPickerFieldId(null);
                    }}
                    onClose={() => setPickerFieldId(null)}
                />
            )}

            {/* One sheet instance for the whole overlay (mirrors PickerSheet) —
                v1's only tenant-reachable host (design D2); the inline variant
                renders per-row from FieldRow instead, behind the same
                `HELP_HOST` constant. Guarded by `!reviewing` defensively: no
                Ask-AI control ever opens a thread in review mode (design D7),
                so this should never render there in practice. */}
            {HELP_HOST === 'sheet' && !reviewing && helpField && (
                <FieldHelpSheet
                    field={helpField}
                    sectionTitle={helpSectionTitle}
                    thread={helpThreads[helpField.id] ?? []}
                    busy={helpBusyFieldIds.has(helpField.id)}
                    slow={helpSlowFieldIds.has(helpField.id)}
                    onSend={(text) => sendHelp(helpField.id, text)}
                    onRetry={(index, text) => retryHelp(helpField.id, index, text)}
                    onClose={closeHelp}
                    lockedMessage={helpFieldLockedMessage}
                />
            )}

            {/* Never for an update (D3/D5): a correction re-post gets the light
                chat acknowledgement in `trySubmit` instead — this success
                screen only ever fires from the first-submit path, but the
                guard is explicit rather than relying on `submitState` never
                reaching 'submitted' while reviewing. */}
            {!reviewing && submitState === 'submitted' && (
                <div className={styles.submittedOverlay} role="alertdialog" aria-label="Form submitted">
                    <span className={styles.submittedCheck} aria-hidden="true">✓</span>
                    <p className={styles.submittedTitle}>{form.short} submitted</p>
                    <p className={styles.submittedSub}>
                        Nice work — the office has everything it needs.
                    </p>
                    <button
                        type="button"
                        className={styles.submitButton}
                        onClick={finishSubmitted}
                        data-testid="cf-submitted-done"
                    >
                        Done
                    </button>
                </div>
            )}
        </div>
    );
};

export default FormOverlay;
